/**
 * Top storage tenants — "which tenant is filling the disk" (#64 Task 4c.1 / U1).
 *
 * #64 shipped the top-N-by-storage API and CLI but no screen, so a platform
 * admin still had no way to see this on the console. This panel renders
 * `GET /api/admin/storage/top-tenants` (the Phase 4b reconcile sweep's
 * at-rest snapshot — NOT a live disk walk) on the Operations destination,
 * beside the other "what needs attention" queues.
 *
 * Modeled directly on `PlatformOverview`'s top-tenants list: same
 * data/loading/error triad, same `useRefreshWhenShown` (this deck stays
 * mounted behind `display: none`, so a mount-only fetch would go stale the
 * moment an operator switches decks and comes back — see `DeckVisibility`),
 * same row shape, with money swapped for `formatBytes`.
 *
 * Scratch (engine app-db) bytes ride along on every row but are rendered on
 * a SEPARATE muted line, never folded into the total — #535's whole point is
 * that they do not count against a tenant's quota.
 */
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from '@papan/i18n'
import { useRefreshWhenShown } from '@papan/core-ui/ui'
import { budgetApi, type TopStorageResponse } from '../../client/budget'
import { Section, formatBytes } from './bits'

export function StorageTopTenants({ onOpenCustomer }: {
  onOpenCustomer: (tenantId: string) => void
}) {
  const { t } = useTranslation('core-ui')
  const [data, setData] = useState<TopStorageResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setData(await budgetApi.getTopStorageTenants())
      setError(null)
    } catch (e: any) {
      setError(e?.message || 'Failed to load top storage tenants')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  // The shelf/settings deck stays MOUNTED behind `display: none`, so the mount
  // effect above is the only fetch this page would ever make — it would serve
  // whatever was true when the deck was first opened, with no error and no way
  // back but a full reload. See `DeckVisibility`.
  useRefreshWhenShown(() => { void load() })

  if (!data) {
    if (loading) return <div className="p-4 themed-text-muted text-sm">{t('common:loading')}</div>
    return <div className="p-4 text-sm text-red-400">{error}</div>
  }

  return (
    <Section
      title={t('settings.platform.storage.title', { defaultValue: 'Top storage tenants' })}
      desc={t('settings.platform.storage.desc', {
        defaultValue:
          'Tenants ranked by drive footprint — live files, bin, versions and quarantine — from the last reconcile sweep.',
      })}
    >
      <div className="themed-bg-secondary rounded-lg overflow-hidden">
        {data.tenants.length === 0 && (
          <div className="px-3 py-4 themed-text-muted text-sm">{t('settings.budget.noData')}</div>
        )}
        {data.tenants.map((row) => (
          <button
            key={row.tenantId}
            onClick={() => onOpenCustomer(row.tenantId)}
            className="w-full text-left px-3 py-2.5 border-b themed-border last:border-b-0 hover:themed-bg-tertiary transition-colors flex items-center gap-3"
          >
            <div className="min-w-0 flex-1">
              <div className="themed-text text-sm truncate">
                {row.name ?? t('settings.platform.storage.deletedTenant', { defaultValue: 'Deleted tenant' })}
              </div>
              <div className="text-[11px] themed-text-muted font-mono truncate">{row.tenantId}</div>
            </div>
            <div className="text-right shrink-0">
              <div className="themed-text text-sm tabular-nums">{formatBytes(row.totalBytes)}</div>
              {row.scratchBytes > 0 && (
                <div className="text-[11px] themed-text-muted tabular-nums">
                  {t('settings.platform.storage.scratchNote', {
                    defaultValue: '+ {{bytes}} scratch — not counted',
                    bytes: formatBytes(row.scratchBytes),
                  })}
                </div>
              )}
            </div>
            <span aria-hidden className="themed-text-muted shrink-0 text-lg leading-none">›</span>
          </button>
        ))}
      </div>
    </Section>
  )
}
